import { useCallback, useContext, useEffect, useRef } from 'react';
import { localPoint } from '@visx/event';
import EventEmitterContext from "../context/EventEmitterContext.js";
/**
 * Hook for optionally subscribing to a specified EventType,
 * and returns emitter for emitting events.
 */
export default function useEventEmitter(/** Type of event to subscribe to. */
eventType, /** Handler invoked on emission of EventType event.  */
handler, /** Optional valid sources for EventType subscription. */
allowedSources) {
  const emitter = useContext(EventEmitterContext);
  const allowedSourcesRef = useRef(undefined);
  allowedSourcesRef.current = allowedSources; // use ref so allowedSources[] can change without creating new handlers

  // wrap emitter.emit so we can enforce stricter type signature
  const emit = useCallback((type, event, source) => {
    if (emitter) {
      emitter.emit(type, {
        event,
        svgPoint: localPoint(event),
        source
      });
    }
  }, [emitter]);
  useEffect(() => {
    if (emitter && eventType && handler) {
      // register handler, with source filtering as needed
      const handlerWithSourceFilter = params => {
        if (!allowedSourcesRef.current || params?.source && allowedSourcesRef.current?.includes(params.source)) {
          handler(params);
        }
      };
      emitter.on(eventType, handlerWithSourceFilter);
      return () => emitter?.off(eventType, handlerWithSourceFilter);
    }
    return undefined;
  }, [emitter, eventType, handler]);
  return emitter ? emit : null;
}